import React, { useEffect, useState, useCallback } from 'react';
import { Pin, PinOff } from 'lucide-react';
import {
  isSpeakerPinned, toggleSpeakerPin, pinnedSpeakersChangeEvent,
} from '../utils/pinnedSpeakers';

/**
 * A compact toggle button used to pin/unpin a speaker to the sidebar.
 * Variants:
 *   - "icon"  → small circular icon (default, used inside cards/rows)
 *   - "pill"  → wider labelled pill (used on speaker detail pages)
 */
const PinSpeakerButton = ({ speaker, variant = 'icon', className = '' }) => {
  const [pinned, setPinned] = useState(false);

  const sync = useCallback(() => {
    setPinned(isSpeakerPinned(speaker?.name));
  }, [speaker?.name]);

  useEffect(() => {
    sync();
    window.addEventListener(pinnedSpeakersChangeEvent, sync);
    return () => window.removeEventListener(pinnedSpeakersChangeEvent, sync);
  }, [sync]);

  const onClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleSpeakerPin(speaker);
  };

  const label = pinned ? 'Unpin from sidebar' : 'Pin to sidebar';
  const Icon = pinned ? PinOff : Pin;

  if (variant === 'pill') {
    return (
      <button
        type="button"
        onClick={onClick}
        title={label}
        aria-label={label}
        data-testid={`pin-speaker-pill-${speaker?.name}`}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
          pinned
            ? 'bg-[#9a7b24] text-white hover:bg-[#7d6119]'
            : 'bg-white border border-[#e5e0d5] text-[#4b5563] hover:border-[#9a7b24] hover:text-[#9a7b24]'
        } ${className}`}
      >
        <Icon className="w-3.5 h-3.5" />
        {pinned ? 'Pinned' : 'Pin to sidebar'}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      data-testid={`pin-speaker-icon-${speaker?.name}`}
      className={`shrink-0 p-1.5 rounded-full transition-colors ${
        pinned
          ? 'bg-[hsl(44,62%,50%)]/25 text-[hsl(44,62%,70%)] hover:bg-[hsl(44,62%,50%)]/40'
          : 'text-white/40 hover:text-white hover:bg-white/10'
      } ${className}`}
    >
      <Icon className="w-3.5 h-3.5" />
    </button>
  );
};

export default PinSpeakerButton;
